import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { normalizeUsPhone } from '@/utils/phone'

export interface ContactByPhoneMatch {
  id: string
  firstName: string | null
  lastName: string | null
  phone: string | null
}

interface LookupResponse {
  success: boolean
  data: { matches: Record<string, ContactByPhoneMatch> }
}

/**
 * Batch phone-number → contact lookup for the Call History tab. Normalizes
 * every input to E.164 client-side, calls the bulk endpoint once, and
 * returns a stable `lookup(phone)` accessor keyed by the normalized form.
 *
 * Cache: 5 minutes stale-time. Contacts rarely change relative to call
 * volume; invalidate by query key `['contacts','lookup-batch', …]` when
 * the user creates a new contact.
 */
export function useContactsByPhone(phones: readonly string[]) {
  const normalizedKeys = useMemo(() => {
    const set = new Set<string>()
    for (const raw of phones) {
      const norm = normalizeUsPhone(raw)
      if (norm) set.add(norm)
    }
    return [...set].sort()
  }, [phones])

  const result = useQuery<Record<string, ContactByPhoneMatch>, Error>({
    queryKey: ['contacts', 'lookup-batch', normalizedKeys],
    queryFn: async () => {
      if (normalizedKeys.length === 0) return {}
      const res = await apiClient.post<LookupResponse>('/api/contacts/lookup-batch', {
        phones: normalizedKeys,
      })
      return res.data?.data?.matches ?? {}
    },
    enabled: normalizedKeys.length > 0,
    staleTime: 5 * 60_000,
  })

  const lookup = useMemo(() => {
    const matches = result.data ?? {}
    return (rawPhone: string | null | undefined): ContactByPhoneMatch | null => {
      if (!rawPhone) return null
      const norm = normalizeUsPhone(rawPhone)
      if (!norm) return null
      return matches[norm] ?? null
    }
  }, [result.data])

  return {
    lookup,
    loading: result.isLoading,
    matches: result.data ?? {},
  }
}
